import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  deleteDoc,
  onSnapshot,
  query,
  where,
  limit,
  orderBy,
  writeBatch,
  arrayUnion,
  arrayRemove,
  deleteField,
  serverTimestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import {
  CoupleSettings,
  GardenWateringState,
  MemoryItem,
  DailyLetter,
  DailyLetterViewRecord,
  MemoryComment,
  VisitorLog,
  VisitorInfo,
  BlockedIpEntry,
  MyWebRequest,
  WebRequestStatus,
  AppDownloadLink,
  WebAnalyticsData,
} from '../types';
import {
  DEFAULT_SETTINGS,
  INITIAL_MEMORIES,
  INITIAL_DAILY_LETTERS,
} from '../data/initialData';
import { getOrCreateVisitorId } from '../utils/visitorDetector';
import { getTodayDateStr } from '../utils/wateringUtils';
import { isUserAdminSync, signInPublicSession } from './authService';

export const PUBLIC_SETTINGS_DOC_REF = doc(db, 'couple_data', 'public');
export const PRIVATE_SETTINGS_DOC_REF = doc(db, 'couple_data', 'private');
export const ADMIN_CONFIG_DOC_REF = doc(db, 'couple_data', 'admin_config');
export const PUBLIC_MEMORIES_COLLECTION_REF = collection(db, 'memories_public');
export const PRIVATE_MEMORIES_COLLECTION_REF = collection(db, 'memories_private');
export const PUBLIC_LETTERS_COLLECTION_REF = collection(db, 'daily_letters_public');
export const PRIVATE_LETTERS_COLLECTION_REF = collection(db, 'daily_letters_private');
const VISITOR_LOGS_COLLECTION_REF = collection(db, 'visitor_logs');
const WEB_REQUESTS_COLLECTION_REF = collection(db, 'web_requests');

/**
 * Recursively strips undefined fields so Firestore setDoc/updateDoc never throws
 * "Unsupported field value: undefined".
 */
export function sanitizeForFirestore<T>(data: T): T {
  if (data === null || data === undefined) {
    return null as any;
  }
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeForFirestore(item)) as any;
  }
  if (typeof data === 'object' && data.constructor === Object) {
    const clean: any = {};
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined) {
        clean[key] = sanitizeForFirestore(value);
      }
    }
    return clean;
  }
  return data;
}

function isSampleMemory(m: any): boolean {
  if (!m) return false;
  if (m.isSample === true) return true;
  const id = String(m.id || '');
  const url = String(m.mediaUrl || '');
  const place = String(m.place || '').toLowerCase();
  if (id.startsWith('sample-') || id.startsWith('sample_') || id === 'mem-1' || id === 'mem-2' || id === 'mem-3') return true;
  if (url.includes('unsplash.com')) return true;
  if (place.includes('mirador del valle') || place.includes('cafetería la esmeralda') || place.includes('playa paraíso')) return true;
  return false;
}

function isSampleDailyLetter(l: any): boolean {
  if (!l) return false;
  if (l.isSample === true) return true;
  const id = String(l.id || '');
  const cover = String(l.coverImage || '');
  if (id.startsWith('letter-sample-') || id.startsWith('sample-') || id.startsWith('sample_') || id === 'dl-1' || id === 'dl-2' || id === 'dl-3' || id === 'letter-1') return true;
  if (cover.includes('unsplash.com')) return true;
  return false;
}

// Connection Status helper
let isFirebaseOnline = false;
export function getFirebaseConnectionStatus(): boolean {
  return isFirebaseOnline;
}

/**
 * Creates a resilient real-time Firestore subscription that automatically handles
 * transient disconnections or long-polling renegotiation with exponential backoff.
 */
function createResilientSubscription(
  attach: (handleErr: (err: any) => void) => Unsubscribe,
  onError?: (error: Error) => void
): Unsubscribe {
  let isCancelled = false;
  let activeUnsub: Unsubscribe | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryDelay = 1500;

  function run() {
    if (isCancelled) return;
    try {
      activeUnsub = attach(handleErr);
    } catch (err: any) {
      handleErr(err);
    }
  }

  function handleErr(err: any) {
    if (isCancelled) return;
    isFirebaseOnline = false;
    if (onError) {
      try {
        onError(err);
      } catch {}
    }

    const isTransient =
      !err ||
      err.code === 'unavailable' ||
      err.code === 'failed-precondition' ||
      err.code === 'deadline-exceeded' ||
      err.message?.includes('unavailable') ||
      err.message?.includes('offline') ||
      err.message?.includes('could not reach');

    if (isTransient) {
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = setTimeout(() => {
        if (!isCancelled) {
          retryDelay = Math.min(retryDelay * 1.5, 8000);
          run();
        }
      }, retryDelay);
    }
  }

  run();

  return () => {
    isCancelled = true;
    if (retryTimer) clearTimeout(retryTimer);
    if (activeUnsub) {
      try {
        activeUnsub();
      } catch {}
    }
  };
}

// ----------------------------------------------------
// 1. SETTINGS SYNC (Separated couple_data/public with settings fallback)
// ----------------------------------------------------
export function subscribeToSettings(
  onUpdate: (settings: CoupleSettings) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return createResilientSubscription((handleErr) => {
    // Read strictly from the hardened couple_data/public document
    return onSnapshot(
      PUBLIC_SETTINGS_DOC_REF,
      (publicSnapshot) => {
        isFirebaseOnline = true;
        if (publicSnapshot.exists()) {
          const data = publicSnapshot.data() as CoupleSettings;
          processAndBroadcastSettings(data, onUpdate);
        } else {
          // Document does not exist yet in Firestore; broadcast clean DEFAULT_SETTINGS without unauthenticated writes
          processAndBroadcastSettings(DEFAULT_SETTINGS, onUpdate);
        }
      },
      (err) => {
        const isOfflineError =
          err?.message?.includes('unavailable') ||
          err?.message?.includes('offline') ||
          err?.code === 'unavailable';
        if (!isOfflineError) {
          console.warn('Firestore settings subscription error:', err);
        }
        if (err?.code === 'permission-denied') {
          try {
            handleFirestoreError(err, OperationType.GET, 'couple_data/public');
          } catch {}
        }
        handleErr(err);
      }
    );
  }, onError);
}

function processAndBroadcastSettings(
  data: CoupleSettings,
  onUpdate: (settings: CoupleSettings) => void
) {
  // Persist avatar backup in localStorage to guarantee it is never lost
  if (typeof window !== 'undefined') {
    try {
      if (data.partner1Avatar) {
        localStorage.setItem('couple_avatar_novio', data.partner1Avatar);
      }
      if (data.partner2Avatar) {
        localStorage.setItem('couple_avatar_novia', data.partner2Avatar);
      }
    } catch {}
  }
  const cachedP1 = typeof window !== 'undefined' ? localStorage.getItem('couple_avatar_novio') || '' : '';
  const cachedP2 = typeof window !== 'undefined' ? localStorage.getItem('couple_avatar_novia') || '' : '';
  const resolvedP1 = data.partner1Avatar || cachedP1;
  const resolvedP2 = data.partner2Avatar || cachedP2;

  const rawPlaylist = Array.isArray(data.playlist) ? data.playlist : [];
  const cleanPlaylist = rawPlaylist.filter(
    (t: any) => !t.isDefault && !['track-1', 'track-2', 'track-3', 'track-4'].includes(t.id)
  );
  const cleanActiveSongId =
    data.activeSongId && ['track-1', 'track-2', 'track-3', 'track-4'].includes(data.activeSongId)
      ? ''
      : data.activeSongId || '';

  // Preserve and synchronize garden watering state with local storage
  const rawGardenWatering: Partial<GardenWateringState> = data.gardenWatering || {};
  let cachedWatering: Partial<GardenWateringState> = {};
  if (typeof window !== 'undefined') {
    try {
      const localW = localStorage.getItem('couple_garden_watering');
      if (localW) cachedWatering = JSON.parse(localW);
    } catch {}
  }

  const remoteWateredTime = rawGardenWatering.lastWateredAt ? Date.parse(rawGardenWatering.lastWateredAt) : 0;
  const cachedWateredTime = cachedWatering.lastWateredAt ? Date.parse(cachedWatering.lastWateredAt) : 0;

  let resolvedWatering: GardenWateringState = {
    ...DEFAULT_SETTINGS.gardenWatering,
    ...rawGardenWatering,
  };

  // If local cache has a newer watering timestamp, respect local action so stale doc doesn't overwrite
  if (cachedWateredTime > remoteWateredTime) {
    resolvedWatering = {
      ...resolvedWatering,
      ...cachedWatering,
      canWaterLevel: typeof cachedWatering.canWaterLevel === 'number' ? cachedWatering.canWaterLevel : resolvedWatering.canWaterLevel,
      flowerWateredAt: cachedWatering.flowerWateredAt || resolvedWatering.flowerWateredAt,
      lastWateredAt: cachedWatering.lastWateredAt || resolvedWatering.lastWateredAt,
      lastCanUsedAt: cachedWatering.lastCanUsedAt !== undefined ? cachedWatering.lastCanUsedAt : resolvedWatering.lastCanUsedAt,
    };
  }

  // Ensure canWaterLevel is a valid number 0-100
  if (typeof resolvedWatering.canWaterLevel !== 'number' || isNaN(resolvedWatering.canWaterLevel)) {
    resolvedWatering.canWaterLevel = 100;
  } else {
    resolvedWatering.canWaterLevel = Math.max(0, Math.min(100, Math.round(resolvedWatering.canWaterLevel)));
  }

  // If the can is currently 100% full, ensure no stale countdown timestamp lingers
  if (resolvedWatering.canWaterLevel >= 100) {
    resolvedWatering.lastCanUsedAt = null as any;
  }

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem('couple_garden_watering', JSON.stringify(resolvedWatering));
      if (data.appDownloadLinks && Array.isArray(data.appDownloadLinks)) {
        localStorage.setItem('couple_app_links_cache', JSON.stringify(data.appDownloadLinks));
      }
    } catch {}
  }

  onUpdate({
    ...DEFAULT_SETTINGS,
    ...data,
    gardenWatering: resolvedWatering,
    playlist: cleanPlaylist,
    activeSongId: cleanActiveSongId,
    partner1Avatar: resolvedP1,
    partner2Avatar: resolvedP2,
  });
}

export async function saveSettingsToDb(settings: CoupleSettings): Promise<void> {
  try {
    const cleanSettings = sanitizeForFirestore(settings);
    // Explicitly delete sensitive admin secrets so they are never leaked in public couple_data
    delete (cleanSettings as any).adminPin;
    delete (cleanSettings as any).adminPinHash;
    delete (cleanSettings as any).partner1PasswordHash;
    delete (cleanSettings as any).partner2PasswordHash;
    delete (cleanSettings as any).adminNotes;
    delete (cleanSettings as any).secret;
    delete (cleanSettings as any).masterPin;
    delete (cleanSettings as any).tokens;
    delete (cleanSettings as any).apiKey;

    // Strip heavy base64 audio strings from playlist so couple_data never exceeds Firestore's 1MB limit
    if (cleanSettings.playlist && Array.isArray(cleanSettings.playlist)) {
      cleanSettings.playlist = cleanSettings.playlist.map((t: any) => ({
        id: t.id,
        title: t.title,
        artist: t.artist,
        duration: t.duration,
        uploadedBy: t.uploadedBy,
        addedAt: t.addedAt,
        isDefault: !!t.isDefault,
        notes: t.notes,
        storageType: t.storageType || 'url',
        url: typeof t.url === 'string' && t.url.startsWith('data:') ? '' : t.url,
      }));
    }

    // Save to public settings document only (no legacy settings doc writes)
    await setDoc(PUBLIC_SETTINGS_DOC_REF, cleanSettings, { merge: true });

    if (typeof window !== 'undefined') {
      try {
        if (cleanSettings.appDownloadLinks && Array.isArray(cleanSettings.appDownloadLinks)) {
          localStorage.setItem('couple_app_links_cache', JSON.stringify(cleanSettings.appDownloadLinks));
        }
      } catch {}
    }
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      handleFirestoreError(err, OperationType.WRITE, 'couple_data/public');
    }
    console.error('Error saving settings to Firestore:', err);
    throw err;
  }
}

/**
 * Actualiza de forma segura y autorizada el modo de mantenimiento en Firestore.
 * Utiliza serverTimestamp() y el UID del administrador autenticado.
 */
export async function updateWebMaintenanceStatusInDb(
  enabled: boolean,
  data?: {
    title?: string;
    message?: string;
    returnTime?: string;
  }
): Promise<void> {
  const currentUid = auth.currentUser?.uid;
  if (!currentUid) {
    throw new Error('Solo un administrador autenticado puede modificar el modo de mantenimiento.');
  }

  const nowIso = new Date().toISOString();
  const title = data?.title?.trim() || 'Estamos preparando algo bonito';
  const message = data?.message?.trim() || 'Estamos realizando algunas mejoras para ofrecerte una mejor experiencia.';
  const returnTime = data?.returnTime?.trim() || '';

  const payload: any = {
    webMaintenance: {
      isEnabled: enabled,
      maintenanceEnabled: enabled,
      title,
      maintenanceTitle: title,
      message,
      maintenanceMessage: message,
      estimatedReturn: returnTime,
      maintenanceReturnTime: returnTime,
      enabledAt: nowIso,
      updatedAt: nowIso,
      enabledBy: currentUid,
      maintenanceUpdatedBy: currentUid,
      maintenanceUpdatedAt: serverTimestamp(),
    },
    maintenanceEnabled: enabled,
    maintenanceTitle: title,
    maintenanceMessage: message,
    maintenanceReturnTime: returnTime,
    maintenanceUpdatedAt: serverTimestamp(),
    maintenanceUpdatedBy: currentUid,
  };

  try {
    await setDoc(PUBLIC_SETTINGS_DOC_REF, payload, { merge: true });
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      handleFirestoreError(err, OperationType.WRITE, 'couple_data/public');
    }
    console.error('Error actualizando modo mantenimiento:', err);
    throw err;
  }
}

/**
 * Guarda exclusivamente la configuración de textos de la pantalla de mantenimiento
 * sin alterar el estado actual de activación (activado/desactivado).
 */
export async function saveWebMaintenanceConfigInDb(config: {
  title: string;
  message: string;
  returnTime?: string;
}): Promise<void> {
  const currentUid = auth.currentUser?.uid;
  if (!currentUid) {
    throw new Error('Solo un administrador autenticado puede guardar la configuración.');
  }

  const nowIso = new Date().toISOString();
  const title = config.title.trim() || 'Estamos preparando algo bonito';
  const message = config.message.trim() || 'Estamos realizando algunas mejoras para ofrecerte una mejor experiencia.';
  const returnTime = config.returnTime?.trim() || '';

  // Get current status first
  const currentSnap = await getDoc(PUBLIC_SETTINGS_DOC_REF);
  const currentData = currentSnap.data() as CoupleSettings | undefined;
  const currentEnabled =
    currentData?.webMaintenance?.isEnabled ??
    currentData?.webMaintenance?.maintenanceEnabled ??
    (currentData as any)?.maintenanceEnabled ??
    false;

  const payload: any = {
    webMaintenance: {
      ...(currentData?.webMaintenance || {}),
      isEnabled: currentEnabled,
      maintenanceEnabled: currentEnabled,
      title,
      maintenanceTitle: title,
      message,
      maintenanceMessage: message,
      estimatedReturn: returnTime,
      maintenanceReturnTime: returnTime,
      updatedAt: nowIso,
      maintenanceUpdatedAt: serverTimestamp(),
      maintenanceUpdatedBy: currentUid,
    },
    maintenanceTitle: title,
    maintenanceMessage: message,
    maintenanceReturnTime: returnTime,
    maintenanceUpdatedAt: serverTimestamp(),
    maintenanceUpdatedBy: currentUid,
  };

  try {
    await setDoc(PUBLIC_SETTINGS_DOC_REF, payload, { merge: true });
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      handleFirestoreError(err, OperationType.WRITE, 'couple_data/public');
    }
    console.error('Error guardando configuración de mantenimiento:', err);
    throw err;
  }
}

/**
 * Authoritative check against Firestore to verify if the primary administrator
 * and initial couple configuration have already been established.
 * Returns strictly a boolean without exposing sensitive credentials, emails, or UIDs.
 */
export async function checkIsInitialSetupCompleted(): Promise<boolean> {
  try {
    // 1. Check authoritative public settings document in Firestore
    const publicDoc = await getDoc(PUBLIC_SETTINGS_DOC_REF);
    if (publicDoc.exists()) {
      const data = publicDoc.data() as CoupleSettings;
      if (
        data?.hasAdminConfigured === true ||
        Boolean(data?.partner1Uid) ||
        Boolean(data?.partner1Email) ||
        Boolean(data?.partner2Uid) ||
        Boolean(data?.partner2Email)
      ) {
        return true;
      }
    }

    // 2. Check system bootstrap configuration document in Firestore
    const bootstrapDoc = await getDoc(doc(db, 'system_config', 'admin_bootstrap'));
    if (bootstrapDoc.exists()) {
      const data = bootstrapDoc.data();
      if (data?.status === 'initialized' || data?.initialSetupCompleted === true) {
        return true;
      }
    }
  } catch (err) {
    console.warn('Error checking initial setup status in Firestore:', err);
  }

  return false;
}

/**
 * Saves administrative configuration to couple_data/admin_config.
 * Only accessible to verified administrators ({ admin: true }).
 */
export async function saveAdminConfigToDb(adminConfig: Record<string, any>): Promise<void> {
  if (!isUserAdminSync(auth.currentUser)) {
    throw new Error('Solo administradores autorizados pueden modificar la configuración administrativa.');
  }
  try {
    const clean = sanitizeForFirestore(adminConfig);
    await setDoc(ADMIN_CONFIG_DOC_REF, clean, { merge: true });
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      handleFirestoreError(err, OperationType.WRITE, 'couple_data/admin_config');
    }
    throw err;
  }
}

/**
 * Authoritatively registers an administrator record in /admins/{uid}
 * so Firestore Rules recognize exists(/databases/$(database)/documents/admins/$(request.auth.uid))
 */
export async function registerAdminRecordInDb(
  uid: string,
  data: { email: string; role: 'novio' | 'novia' | 'admin' }
): Promise<void> {
  if (!uid) return;
  try {
    const adminDocRef = doc(db, 'admins', uid);
    await setDoc(
      adminDocRef,
      {
        uid,
        email: data.email.toLowerCase().trim(),
        role: data.role,
        isAdmin: true,
        updatedAt: new Date().toISOString(),
      },
      { merge: true }
    );
  } catch (err: any) {
    console.warn('[registerAdminRecordInDb]', err?.message || err);
  }

  try {
    await fetch('/api/admin/register-admin-uid', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid, email: data.email, role: data.role }),
    });
  } catch {}
}

/**
 * Reads administrative configuration from couple_data/admin_config.
 */
export async function getAdminConfigFromDb(): Promise<Record<string, any> | null> {
  if (!isUserAdminSync(auth.currentUser)) return null;
  try {
    const snap = await getDoc(ADMIN_CONFIG_DOC_REF);
    return snap.exists() ? snap.data() : null;
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      handleFirestoreError(err, OperationType.GET, 'couple_data/admin_config');
    }
    return null;
  }
}

/**
 * Saves private couple vault data to couple_data/private.
 */
export async function savePrivateCoupleData(privateData: Record<string, any>): Promise<void> {
  if (!isUserAdminSync(auth.currentUser)) {
    throw new Error('Solo administradores autorizados pueden modificar el baúl privado de la pareja.');
  }
  try {
    const clean = sanitizeForFirestore(privateData);
    await setDoc(PRIVATE_SETTINGS_DOC_REF, clean, { merge: true });
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      handleFirestoreError(err, OperationType.WRITE, 'couple_data/private');
    }
    throw err;
  }
}

/**
 * Reads private couple vault data from couple_data/private.
 */
export async function getPrivateCoupleData(): Promise<Record<string, any> | null> {
  if (!isUserAdminSync(auth.currentUser)) return null;
  try {
    const snap = await getDoc(PRIVATE_SETTINGS_DOC_REF);
    return snap.exists() ? snap.data() : null;
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      handleFirestoreError(err, OperationType.GET, 'couple_data/private');
    }
    return null;
  }
}

/**
 * Updates garden watering status in Firestore and localStorage.
 * Ensures instant real-time sync between partners and visitors.
 */
export async function recordWateringInDb(
  wateringState: Partial<GardenWateringState>
): Promise<GardenWateringState> {
  const localKey = 'couple_garden_watering';
  let cached: any = {};
  if (typeof window !== 'undefined') {
    try {
      const localStr = localStorage.getItem(localKey);
      if (localStr) cached = JSON.parse(localStr);
    } catch {}
  }

  const nowIso = new Date().toISOString();
  const baseWatering = {
    ...DEFAULT_SETTINGS.gardenWatering,
    ...cached,
  };

  const nextLevel =
    wateringState.canWaterLevel !== undefined
      ? Math.max(0, Math.min(100, Math.round(wateringState.canWaterLevel)))
      : typeof baseWatering.canWaterLevel === 'number'
      ? baseWatering.canWaterLevel
      : 100;

  const mergedWatering: GardenWateringState = {
    ...baseWatering,
    ...wateringState,
    lastWateredAt: wateringState.lastWateredAt || nowIso,
    wateredCount:
      wateringState.wateredCount !== undefined
        ? wateringState.wateredCount
        : (baseWatering.wateredCount || 0) + 1,
    wateringIntervalDays:
      wateringState.wateringIntervalDays !== undefined
        ? wateringState.wateringIntervalDays
        : baseWatering.wateringIntervalDays || 3,
    canWaterLevel: nextLevel,
    lastWaterRefillDate:
      wateringState.lastWaterRefillDate ||
      baseWatering.lastWaterRefillDate ||
      getTodayDateStr(),
    lastCanUsedAt:
      nextLevel >= 100
        ? (null as any)
        : wateringState.lastCanUsedAt !== undefined
        ? wateringState.lastCanUsedAt
        : baseWatering.lastCanUsedAt || nowIso,
    flowerWateredAt:
      wateringState.flowerWateredAt ||
      baseWatering.flowerWateredAt || [
        nowIso,
        nowIso,
        nowIso,
        nowIso,
      ],
  };

  // 1. Instant local persistence & custom event broadcast for 0ms reactivity
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(localKey, JSON.stringify(mergedWatering));
      window.dispatchEvent(
        new CustomEvent('couple_garden_watering_changed', { detail: mergedWatering })
      );
    } catch {}
  }

  // 2. Synchronize to Firestore public settings document
  try {
    if (!auth.currentUser) {
      try {
        await signInPublicSession();
      } catch {}
    }

    await setDoc(
      PUBLIC_SETTINGS_DOC_REF,
      sanitizeForFirestore({
        gardenWatering: mergedWatering,
      }),
      { merge: true }
    );
  } catch (err: any) {
    console.warn('Could not sync garden watering to Firestore (preserved locally):', err);
  }

  return mergedWatering;
}

/**
 * Saves a partner profile avatar (novio or novia) directly to Firestore and local storage.
 * Ensures the avatar is permanently preserved and not lost during general settings sync.
 */
export async function saveProfileAvatarToDb(
  profile: 'novio' | 'novia',
  avatarBase64: string
): Promise<void> {
  const field = profile === 'novio' ? 'partner1Avatar' : 'partner2Avatar';
  const cacheKey = `couple_avatar_${profile}`;

  if (typeof window !== 'undefined') {
    try {
      if (avatarBase64) {
        localStorage.setItem(cacheKey, avatarBase64);
      } else {
        localStorage.removeItem(cacheKey);
      }
    } catch {}
  }

  try {
    await setDoc(
      PUBLIC_SETTINGS_DOC_REF,
      sanitizeForFirestore({
        [field]: avatarBase64 || '',
      }),
      { merge: true }
    );
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      handleFirestoreError(err, OperationType.WRITE, 'couple_data/public');
    }
    console.error(`Error saving ${profile} avatar to Firestore:`, err);
    throw err;
  }
}

/**
 * Adds an IP address to the blocked list in settings instantly using atomic Firestore operations
 */
export async function blockIpAddressInDb(
  ip: string,
  meta?: {
    reason?: string;
    deviceModel?: string;
    visitorId?: string;
    country?: string;
    city?: string;
    flagEmoji?: string;
    blockedBy?: 'novio' | 'novia';
  }
): Promise<void> {
  if (!ip || ip === 'IP no detectada') return;
  const cleanIp = ip.trim();
  const sanitizedKey = cleanIp.replace(/[^a-zA-Z0-9]/g, '_');
  const now = new Date().toISOString();
  const entry: BlockedIpEntry = {
    ip: cleanIp,
    blockedAt: now,
    reason: meta?.reason || 'Actividad sospechosa detectada',
    deviceModel: meta?.deviceModel || '',
    visitorId: meta?.visitorId || '',
    country: meta?.country || '',
    city: meta?.city || '',
    flagEmoji: meta?.flagEmoji || '',
    blockedBy: meta?.blockedBy || 'novio',
  };

  try {
    const updatePayload: Record<string, any> = {
      blockedIps: arrayUnion(cleanIp),
      [`blockedIpEntries.${sanitizedKey}`]: sanitizeForFirestore(entry),
    };
    if (meta?.visitorId) {
      updatePayload['blockedVisitorIds'] = arrayUnion(meta.visitorId);
    }
    // High-speed atomic update in <100ms
    await updateDoc(PUBLIC_SETTINGS_DOC_REF, updatePayload);
  } catch (err: any) {
    // If updateDoc fails (e.g. document not initialized yet or permissions), fallback with merge
    try {
      const snap = await getDoc(PUBLIC_SETTINGS_DOC_REF);
      const currentSettings = snap.exists() ? (snap.data() as CoupleSettings) : ({} as CoupleSettings);
      const currentBlocked = Array.isArray(currentSettings.blockedIps) ? [...currentSettings.blockedIps] : [];
      if (!currentBlocked.includes(cleanIp)) currentBlocked.push(cleanIp);
      const currentVisitorIds = Array.isArray(currentSettings.blockedVisitorIds) ? [...currentSettings.blockedVisitorIds] : [];
      if (meta?.visitorId && !currentVisitorIds.includes(meta.visitorId)) {
        currentVisitorIds.push(meta.visitorId);
      }

      await setDoc(
        PUBLIC_SETTINGS_DOC_REF,
        sanitizeForFirestore({
          blockedIps: currentBlocked,
          blockedVisitorIds: currentVisitorIds,
          blockedIpEntries: {
            ...(currentSettings.blockedIpEntries || {}),
            [sanitizedKey]: entry,
          },
        }),
        { merge: true }
      );
    } catch (fallbackErr: any) {
      if (fallbackErr?.code === 'permission-denied') {
        handleFirestoreError(fallbackErr, OperationType.WRITE, 'couple_data/public');
      }
      console.error('Error blocking IP in Firestore:', fallbackErr);
      throw fallbackErr;
    }
  }
}

/**
 * Removes an IP address from the blocked list in settings instantly
 */
export async function unblockIpAddressInDb(ip: string, visitorId?: string): Promise<void> {
  if (!ip) return;
  const cleanIp = ip.trim();
  const sanitizedKey = cleanIp.replace(/[^a-zA-Z0-9]/g, '_');
  try {
    const updatePayload: Record<string, any> = {
      blockedIps: arrayRemove(cleanIp),
      [`blockedIpEntries.${sanitizedKey}`]: deleteField(),
    };
    if (visitorId) {
      updatePayload['blockedVisitorIds'] = arrayRemove(visitorId);
    }
    await updateDoc(PUBLIC_SETTINGS_DOC_REF, updatePayload);
  } catch (err: any) {
    // Fallback if needed
    try {
      const snap = await getDoc(PUBLIC_SETTINGS_DOC_REF);
      if (!snap.exists()) return;
      const currentSettings = snap.data() as CoupleSettings;
      const currentBlocked = Array.isArray(currentSettings.blockedIps) ? [...currentSettings.blockedIps] : [];
      const updatedBlocked = currentBlocked.filter((item) => item !== cleanIp);
      const updatedVisitorIds = visitorId && Array.isArray(currentSettings.blockedVisitorIds)
        ? currentSettings.blockedVisitorIds.filter((v) => v !== visitorId)
        : currentSettings.blockedVisitorIds;
      const updatedEntries = { ...(currentSettings.blockedIpEntries || {}) };
      delete updatedEntries[sanitizedKey];

      await setDoc(
        PUBLIC_SETTINGS_DOC_REF,
        sanitizeForFirestore({
          blockedIps: updatedBlocked,
          blockedVisitorIds: updatedVisitorIds,
          blockedIpEntries: updatedEntries,
        }),
        { merge: true }
      );
    } catch (fallbackErr: any) {
      if (fallbackErr?.code === 'permission-denied') {
        handleFirestoreError(fallbackErr, OperationType.WRITE, 'couple_data/public');
      }
      console.error('Error unblocking IP in Firestore:', fallbackErr);
      throw fallbackErr;
    }
  }
}

// ----------------------------------------------------
// 2. MEMORIES SYNC (Separated Public and Private Collections)
// ----------------------------------------------------
export function subscribeToMemories(
  onUpdate: (memories: MemoryItem[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return createResilientSubscription((handleErr) => {
    let publicMemories: MemoryItem[] = [];
    let privateMemories: MemoryItem[] = [];

    const notifyCombined = () => {
      const combinedMap = new Map<string, MemoryItem>();
      publicMemories.forEach((m) => combinedMap.set(m.id, m));
      privateMemories.forEach((m) => combinedMap.set(m.id, m));
      const list = Array.from(combinedMap.values());

      list.sort((a, b) => {
        const timeA = new Date(a.date || a.createdAt || 0).getTime();
        const timeB = new Date(b.date || b.createdAt || 0).getTime();
        return timeB - timeA;
      });

      onUpdate(list);
    };

    // Public memories listener (available to all visitors)
    const unsubPublic = onSnapshot(
      query(PUBLIC_MEMORIES_COLLECTION_REF),
      (snapshot) => {
        isFirebaseOnline = true;
        const list: MemoryItem[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as MemoryItem;
          list.push({
            ...data,
            id: docSnap.id,
            comments: Array.isArray(data.comments) ? data.comments : [],
          });
        });
        publicMemories = list;
        notifyCombined();
      },
      (err) => {
        const isOfflineError =
          err?.message?.includes('unavailable') ||
          err?.message?.includes('offline') ||
          err?.code === 'unavailable';
        if (!isOfflineError) {
          console.warn('Firestore memories subscription error:', err);
        }
        if (err?.code === 'permission-denied') {
          try {
            handleFirestoreError(err, OperationType.LIST, 'memories_public');
          } catch {}
        }
        handleErr(err);
      }
    );

    // Private memories listener (for authenticated owner with ownerUid == uid, or all for admin)
    let unsubPrivate: (() => void) | null = null;

    const setupPrivateMemories = (user: any) => {
      if (unsubPrivate) {
        unsubPrivate();
        unsubPrivate = null;
      }
      privateMemories = [];

      const isAdmin = isUserAdminSync(user);
      if (isAdmin) {
        try {
          unsubPrivate = onSnapshot(
            query(PRIVATE_MEMORIES_COLLECTION_REF),
            (snapshot) => {
              const list: MemoryItem[] = [];
              snapshot.forEach((docSnap) => {
                const data = docSnap.data() as MemoryItem;
                list.push({
                  ...data,
                  id: docSnap.id,
                  comments: Array.isArray(data.comments) ? data.comments : [],
                });
              });
              privateMemories = list;
              notifyCombined();
            },
            () => {}
          );
        } catch {}
      } else if (user && user.uid) {
        try {
          unsubPrivate = onSnapshot(
            query(PRIVATE_MEMORIES_COLLECTION_REF, where('ownerUid', '==', user.uid)),
            (snapshot) => {
              const list: MemoryItem[] = [];
              snapshot.forEach((docSnap) => {
                const data = docSnap.data() as MemoryItem;
                list.push({
                  ...data,
                  id: docSnap.id,
                  comments: Array.isArray(data.comments) ? data.comments : [],
                });
              });
              privateMemories = list;
              notifyCombined();
            },
            () => {}
          );
        } catch {}
      } else {
        notifyCombined();
      }
    };

    const authUnsub = auth.onAuthStateChanged((user) => {
      setupPrivateMemories(user);
    });

    return () => {
      unsubPublic();
      if (unsubPrivate) unsubPrivate();
      authUnsub();
    };
  }, onError);
}

export async function saveMemoryToDb(memory: MemoryItem): Promise<void> {
  try {
    const isPrivate = Boolean(memory.isPrivate);
    const targetColl = isPrivate ? PRIVATE_MEMORIES_COLLECTION_REF : PUBLIC_MEMORIES_COLLECTION_REF;
    const memoryDoc = doc(targetColl, memory.id);
    const cleanMemory = sanitizeForFirestore(memory);
    if (!cleanMemory.ownerUid && auth.currentUser) {
      cleanMemory.ownerUid = auth.currentUser.uid;
    }
    await setDoc(memoryDoc, cleanMemory, { merge: true });

    // Clean up opposing collection if privacy was changed
    const opposingColl = isPrivate ? PUBLIC_MEMORIES_COLLECTION_REF : PRIVATE_MEMORIES_COLLECTION_REF;
    await deleteDoc(doc(opposingColl, memory.id)).catch(() => {});
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      const isPrivate = Boolean(memory.isPrivate);
      handleFirestoreError(
        err,
        OperationType.WRITE,
        isPrivate ? `memories_private/${memory.id}` : `memories_public/${memory.id}`
      );
    }
    console.error('Error saving memory to Firestore:', err);
    throw err;
  }
}

export async function deleteMemoryFromDb(id: string): Promise<void> {
  try {
    await Promise.all([
      deleteDoc(doc(PUBLIC_MEMORIES_COLLECTION_REF, id)).catch(() => {}),
      deleteDoc(doc(PRIVATE_MEMORIES_COLLECTION_REF, id)).catch(() => {}),
    ]);
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      handleFirestoreError(err, OperationType.DELETE, `memories_public/${id}`);
    }
    console.error('Error deleting memory from Firestore:', err);
    throw err;
  }
}

export async function addCommentToMemoryInDb(
  memoryId: string,
  comment: MemoryComment,
  currentComments: MemoryComment[]
): Promise<void> {
  try {
    const user = auth.currentUser || (await signInPublicSession());
    const cleanText = (comment.text || '').trim().slice(0, 1000);
    const cleanAuthor = (comment.author || 'Visitante').trim().slice(0, 60);

    if (user && cleanText) {
      const commentDoc = doc(PUBLIC_MEMORIES_COLLECTION_REF, memoryId, 'comments', comment.id);
      await setDoc(commentDoc, {
        id: comment.id,
        text: cleanText,
        author: cleanAuthor,
        authorUid: user.uid,
        createdAt: comment.createdAt || new Date().toISOString(),
      });
    }

    // If authorized admin/owner, sync memory comments array
    if (isUserAdminSync(auth.currentUser)) {
      const memoryDoc = doc(PUBLIC_MEMORIES_COLLECTION_REF, memoryId);
      const updated = [...currentComments, { ...comment, text: cleanText, author: cleanAuthor }];
      await setDoc(memoryDoc, { comments: updated }, { merge: true }).catch(() => {});
    }
  } catch (err) {
    console.error('Error adding comment to memory in Firestore:', err);
    throw err;
  }
}

// ----------------------------------------------------
// 3. DAILY LETTERS SYNC (Separated Public and Private Collections)
// ----------------------------------------------------
export function subscribeToDailyLetters(
  onUpdate: (letters: DailyLetter[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return createResilientSubscription((handleErr) => {
    let publicLetters: DailyLetter[] = [];
    let privateLetters: DailyLetter[] = [];

    const notifyCombined = () => {
      const combinedMap = new Map<string, DailyLetter>();
      publicLetters.forEach((l) => combinedMap.set(l.id, l));
      privateLetters.forEach((l) => combinedMap.set(l.id, l));
      const list = Array.from(combinedMap.values());

      list.sort((a, b) => {
        const dateA = a.unlockDate || a.date || a.createdAt || '';
        const dateB = b.unlockDate || b.date || b.createdAt || '';
        return dateB.localeCompare(dateA);
      });

      onUpdate(list);
    };

    // Public letters listener (available to all visitors)
    const unsubPublic = onSnapshot(
      query(PUBLIC_LETTERS_COLLECTION_REF),
      (snapshot) => {
        isFirebaseOnline = true;
        const list: DailyLetter[] = [];
        snapshot.forEach((docSnap) => {
          list.push({ ...(docSnap.data() as DailyLetter), id: docSnap.id });
        });
        publicLetters = list;
        notifyCombined();
      },
      (err) => {
        const isOfflineError =
          err?.message?.includes('unavailable') ||
          err?.message?.includes('offline') ||
          err?.code === 'unavailable';
        if (!isOfflineError) {
          console.warn('Firestore daily letters subscription error:', err);
        }
        if (err?.code === 'permission-denied') {
          try {
            handleFirestoreError(err, OperationType.LIST, 'daily_letters_public');
          } catch {}
        }
        handleErr(err);
      }
    );

    // Private letters listener (for authenticated owner with ownerUid == uid, or all for admin)
    let unsubPrivate: (() => void) | null = null;

    const setupPrivateLetters = (user: any) => {
      if (unsubPrivate) {
        unsubPrivate();
        unsubPrivate = null;
      }
      privateLetters = [];

      const isAdmin = isUserAdminSync(user);
      if (isAdmin) {
        try {
          unsubPrivate = onSnapshot(
            query(PRIVATE_LETTERS_COLLECTION_REF),
            (snapshot) => {
              const list: DailyLetter[] = [];
              snapshot.forEach((docSnap) => {
                list.push({ ...(docSnap.data() as DailyLetter), id: docSnap.id });
              });
              privateLetters = list;
              notifyCombined();
            },
            () => {}
          );
        } catch {}
      } else if (user && user.uid) {
        try {
          unsubPrivate = onSnapshot(
            query(PRIVATE_LETTERS_COLLECTION_REF, where('ownerUid', '==', user.uid)),
            (snapshot) => {
              const list: DailyLetter[] = [];
              snapshot.forEach((docSnap) => {
                list.push({ ...(docSnap.data() as DailyLetter), id: docSnap.id });
              });
              privateLetters = list;
              notifyCombined();
            },
            () => {}
          );
        } catch {}
      } else {
        notifyCombined();
      }
    };

    const authUnsub = auth.onAuthStateChanged((user) => {
      setupPrivateLetters(user);
    });

    return () => {
      unsubPublic();
      if (unsubPrivate) unsubPrivate();
      authUnsub();
    };
  }, onError);
}

export async function saveDailyLetterToDb(letter: DailyLetter): Promise<void> {
  try {
    const isPrivate = Boolean(letter.isPrivate);
    const targetColl = isPrivate ? PRIVATE_LETTERS_COLLECTION_REF : PUBLIC_LETTERS_COLLECTION_REF;
    const letterDoc = doc(targetColl, letter.id);
    const cleanLetter = sanitizeForFirestore(letter);
    if (!cleanLetter.ownerUid && auth.currentUser) {
      cleanLetter.ownerUid = auth.currentUser.uid;
    }
    await setDoc(letterDoc, cleanLetter, { merge: true });

    // Clean up opposing collection if privacy was changed
    const opposingColl = isPrivate ? PUBLIC_LETTERS_COLLECTION_REF : PRIVATE_LETTERS_COLLECTION_REF;
    await deleteDoc(doc(opposingColl, letter.id)).catch(() => {});
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      const isPrivate = Boolean(letter.isPrivate);
      handleFirestoreError(
        err,
        OperationType.WRITE,
        isPrivate ? `daily_letters_private/${letter.id}` : `daily_letters_public/${letter.id}`
      );
    }
    console.error('Error saving daily letter to Firestore:', err);
    throw err;
  }
}

export async function deleteDailyLetterFromDb(id: string): Promise<void> {
  try {
    await Promise.all([
      deleteDoc(doc(PUBLIC_LETTERS_COLLECTION_REF, id)).catch(() => {}),
      deleteDoc(doc(PRIVATE_LETTERS_COLLECTION_REF, id)).catch(() => {}),
    ]);
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      handleFirestoreError(err, OperationType.DELETE, `daily_letters_public/${id}`);
    }
    console.error('Error deleting daily letter from Firestore:', err);
    throw err;
  }
}

export async function toggleDailyLetterUnlockInDb(
  id: string,
  isUnlocked: boolean
): Promise<void> {
  try {
    const publicDoc = doc(PUBLIC_LETTERS_COLLECTION_REF, id);
    const snap = await getDoc(publicDoc);
    if (snap.exists()) {
      await setDoc(publicDoc, { isUnlocked }, { merge: true });
    } else {
      const privDoc = doc(PRIVATE_LETTERS_COLLECTION_REF, id);
      await setDoc(privDoc, { isUnlocked }, { merge: true }).catch(() => {});
    }
  } catch (err) {
    console.error('Error toggling daily letter unlock in Firestore:', err);
  }
}

export async function toggleDailyLetterFavoriteInDb(
  id: string,
  isFavorite: boolean
): Promise<void> {
  try {
    const publicDoc = doc(PUBLIC_LETTERS_COLLECTION_REF, id);
    const snap = await getDoc(publicDoc);
    if (snap.exists()) {
      await setDoc(publicDoc, { isFavorite }, { merge: true });
    } else {
      const privDoc = doc(PRIVATE_LETTERS_COLLECTION_REF, id);
      await setDoc(privDoc, { isFavorite }, { merge: true }).catch(() => {});
    }
  } catch (err) {
    console.error('Error toggling daily letter favorite in Firestore:', err);
  }
}

export async function toggleDailyLetterPrivacyInDb(
  id: string,
  isPrivate: boolean
): Promise<void> {
  try {
    const currentRef = isPrivate ? doc(PUBLIC_LETTERS_COLLECTION_REF, id) : doc(PRIVATE_LETTERS_COLLECTION_REF, id);
    const snap = await getDoc(currentRef);
    if (snap.exists()) {
      const data = snap.data() as DailyLetter;
      await saveDailyLetterToDb({ ...data, id, isPrivate });
    }
  } catch (err) {
    console.error('Error toggling daily letter privacy in Firestore:', err);
  }
}

export async function recordDailyLetterOpenInDb(
  id: string,
  viewInfo: DailyLetterViewRecord
): Promise<void> {
  try {
    const publicDoc = doc(PUBLIC_LETTERS_COLLECTION_REF, id);
    let targetDoc = publicDoc;
    let snap = await getDoc(publicDoc);
    if (!snap.exists()) {
      targetDoc = doc(PRIVATE_LETTERS_COLLECTION_REF, id);
      snap = await getDoc(targetDoc);
    }
    const data = snap.data() as DailyLetter | undefined;
    const existingViews = data?.views || [];
    const newViews = [viewInfo, ...existingViews.filter((v) => v.openedAt !== viewInfo.openedAt)].slice(0, 50);

    await setDoc(
      targetDoc,
      {
        isRead: true,
        firstOpenedAt: data?.firstOpenedAt || viewInfo.openedAt,
        lastOpenedAt: viewInfo.openedAt,
        openedBy: viewInfo.viewerName,
        views: newViews,
      },
      { merge: true }
    );
  } catch (err) {
    console.error('Error recording daily letter open in Firestore:', err);
  }
}

// ----------------------------------------------------
// 5. PURGE & CLEAN SAMPLE DATA (Real-time Reset)
// ----------------------------------------------------
/**
 * Safely removes exclusively demonstration/sample data from Firestore.
 * - Guaranteed NEVER to delete real user memories or real user letters.
 * - Requires verified Firebase Admin custom claims.
 * - Selectively deletes documents matching explicit sample flags or sample IDs.
 */
export async function purgeAllSampleData(options?: {
  customAuth?: any;
  customDb?: any;
}): Promise<{ deletedMemories: number; deletedLetters: number }> {
  const activeAuth = options?.customAuth || auth;
  const activeDb = options?.customDb || db;

  // 1. Explicit admin verification: rejects immediately if caller is not an admin
  if (!isUserAdminSync(activeAuth.currentUser)) {
    console.warn('Unauthorized purgeAllSampleData invocation: Admin privileges required.');
    throw new Error('Operación no autorizada: Se requieren credenciales de administrador.');
  }

  try {
    // 2. Fetch documents to selectively inspect for sample markers
    const pubMemColl = collection(activeDb, 'memories_public');
    const privMemColl = collection(activeDb, 'memories_private');
    const pubLetterColl = collection(activeDb, 'daily_letters_public');
    const privLetterColl = collection(activeDb, 'daily_letters_private');

    const [pubMemDocs, privMemDocs, pubLetterDocs, privLetterDocs] = await Promise.all([
      getDocs(pubMemColl),
      getDocs(privMemColl),
      getDocs(pubLetterColl),
      getDocs(privLetterColl),
    ]);

    const batch = writeBatch(activeDb);
    let deletedMemories = 0;
    let deletedLetters = 0;

    // NEVER delete real user documents. Only queue documents unequivocally marked as samples
    const checkAndQueueMemory = (d: any) => {
      const data = d.data();
      const id = String(d.id || '');
      const isExplicitSample =
        data.isSample === true ||
        id.startsWith('sample-') ||
        id.startsWith('sample_') ||
        id === 'mem-1' ||
        id === 'mem-2' ||
        id === 'mem-3';
      if (isExplicitSample) {
        batch.delete(d.ref);
        deletedMemories++;
      }
    };

    const checkAndQueueLetter = (d: any) => {
      const data = d.data();
      const id = String(d.id || '');
      const isExplicitSample =
        data.isSample === true ||
        id.startsWith('letter-sample-') ||
        id.startsWith('sample-') ||
        id.startsWith('sample_') ||
        id === 'dl-1' ||
        id === 'dl-2' ||
        id === 'dl-3' ||
        id === 'letter-1';
      if (isExplicitSample) {
        batch.delete(d.ref);
        deletedLetters++;
      }
    };

    pubMemDocs.forEach(checkAndQueueMemory);
    privMemDocs.forEach(checkAndQueueMemory);
    pubLetterDocs.forEach(checkAndQueueLetter);
    privLetterDocs.forEach(checkAndQueueLetter);

    if (deletedMemories > 0 || deletedLetters > 0) {
      await batch.commit();
    }

    return { deletedMemories, deletedLetters };
  } catch (err) {
    console.error('Error selectively purging sample data from Firestore:', err);
    throw err;
  }
}

// ----------------------------------------------------
// 6. VISITOR TRACKING & REGISTRY (Real-time Collection)
// ----------------------------------------------------

/**
 * Subscribes to the visitor logs collection in real time
 */
/**
 * Subscribes to real-time updates of visitor logs in Firestore with intelligent device grouping
 */
export function subscribeToVisitorLogs(
  onUpdate: (logs: VisitorLog[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  // Security guard: visitors cannot read visitor logs
  if (!isUserAdminSync(auth.currentUser)) {
    return () => {};
  }

  return createResilientSubscription((handleErr) => {
    const q = query(VISITOR_LOGS_COLLECTION_REF);
    return onSnapshot(
      q,
      (snapshot) => {
        const rawList: VisitorLog[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();

        // Build array of recorded IPs
        const ipHistory: string[] = [];
        if (data.ip && typeof data.ip === 'string' && data.ip !== 'IP no detectada' && data.ip !== 'Desconocida') {
          ipHistory.push(data.ip);
        }
        if (Array.isArray(data.ipHistory)) {
          for (const item of data.ipHistory) {
            if (typeof item === 'string' && item.trim() && !ipHistory.includes(item.trim())) {
              ipHistory.push(item.trim());
            }
          }
        }

        const lastActiveAt = data.lastActiveAt || data.lastVisitedAt || new Date().toISOString();
        const diffMs = Date.now() - new Date(lastActiveAt).getTime();
        const isOnline = diffMs < 45000; // Active within the last 45 seconds

        rawList.push({
          id: docSnap.id,
          ip: data.ip || 'Desconocida',
          visitorId: data.visitorId || docSnap.id,
          docId: docSnap.id,
          deviceType: data.deviceType || 'Escritorio',

          // Real Hardware Brand & Model
          brand: data.brand || (data.deviceType === 'Móvil' ? 'Smartphone' : 'Computadora'),
          model: data.model || (data.deviceType === 'Móvil' ? 'Dispositivo Móvil' : 'PC Estándar'),
          os: data.os || 'Desconocido',
          osVersion: data.osVersion || '',
          browser: data.browser || 'Desconocido',
          browserVersion: data.browserVersion || '',

          // Real Display & Screen
          screenResolution: data.screenResolution || 'Desconocida',
          physicalScreenResolution: data.physicalScreenResolution || data.screenResolution || 'Desconocida',
          devicePixelRatio: typeof data.devicePixelRatio === 'number' ? data.devicePixelRatio : 1,
          colorDepth: data.colorDepth || '24 bits',
          orientation: data.orientation || 'Vertical (Portrait)',

          // Hardware Silicon & MAC status
          gpuRenderer: data.gpuRenderer || 'Acelerador Estándar',
          gpuVendor: data.gpuVendor || 'Genérico',
          cpuCores: typeof data.cpuCores === 'number' ? data.cpuCores : 4,
          deviceMemory: data.deviceMemory,
          maxTouchPoints: typeof data.maxTouchPoints === 'number' ? data.maxTouchPoints : 0,
          connectionType: data.connectionType,
          batteryLevel: data.batteryLevel,
          macStatus: data.macStatus || 'MAC física aislada por Sandbox W3C de navegador. Identificado mediante Huella Digital de Hardware Única (HW-UUID).',
          deviceFingerprint: data.deviceFingerprint || `HW-${docSnap.id.substring(0, 4).toUpperCase()}-42E0`,
          hardwareSignature: data.hardwareSignature,

          language: data.language || 'es',
          timezone: data.timezone || 'UTC',
          userAgent: data.userAgent || '',
          firstVisitedAt: data.firstVisitedAt || new Date().toISOString(),
          lastVisitedAt: data.lastVisitedAt || new Date().toISOString(),
          lastActiveAt,
          isOnline,
          visitCount: typeof data.visitCount === 'number' ? data.visitCount : 1,
          ipHistory: ipHistory.length > 0 ? ipHistory : [data.ip || 'Desconocida'],
        });
      });

      // =========================================================================
      // INTELLIGENT DEVICE GROUPING:
      // If multiple records belong to the same device (same visitorId OR same
      // deviceFingerprint OR same hardware signature with different IPs),
      // we merge them into the SAME single device card (mismo recuadro)!
      // =========================================================================
      const groupedMap = new Map<string, VisitorLog>();

      for (const log of rawList) {
        // Compute grouping key: prefers visitorId or deviceFingerprint
        let groupKey = log.visitorId;
        if (!groupKey || groupKey === log.id) {
          if (log.deviceFingerprint) {
            groupKey = log.deviceFingerprint;
          } else if (log.hardwareSignature) {
            groupKey = log.hardwareSignature;
          } else {
            // Fallback to stable hardware fingerprint key
            groupKey = `${log.brand}_${log.model}_${log.physicalScreenResolution}_${log.gpuRenderer}`.toLowerCase();
          }
        }

        const existing = groupedMap.get(groupKey);
        if (!existing) {
          groupedMap.set(groupKey, { ...log });
        } else {
          // Merge into the same single box!
          const allIps = Array.from(
            new Set([...(existing.ipHistory || []), ...(log.ipHistory || []), log.ip, existing.ip].filter(Boolean))
          );

          // Find the most recent record
          const existingTime = new Date(existing.lastVisitedAt || 0).getTime();
          const logTime = new Date(log.lastVisitedAt || 0).getTime();
          const mostRecent = logTime >= existingTime ? log : existing;
          const oldestFirstVisit =
            new Date(existing.firstVisitedAt || 0).getTime() <= new Date(log.firstVisitedAt || 0).getTime()
              ? existing.firstVisitedAt
              : log.firstVisitedAt;

          const mergedVisitCount = (existing.visitCount || 1) + (log.visitCount || 1);

          const latestActive =
            new Date(existing.lastActiveAt || 0).getTime() >= new Date(log.lastActiveAt || 0).getTime()
              ? existing.lastActiveAt
              : log.lastActiveAt;
          const isOnlineNow = Boolean(
            latestActive && Date.now() - new Date(latestActive).getTime() < 45000
          );

          groupedMap.set(groupKey, {
            ...mostRecent,
            ip: mostRecent.ip,
            ipHistory: allIps,
            visitCount: mergedVisitCount,
            firstVisitedAt: oldestFirstVisit,
            lastVisitedAt: mostRecent.lastVisitedAt,
            lastActiveAt: latestActive,
            isOnline: isOnlineNow,
          });
        }
      }

      const list = Array.from(groupedMap.values());

      // Sort: Online devices first (🟢), then by most recent activity
      list.sort((a, b) => {
        if (a.isOnline && !b.isOnline) return -1;
        if (!a.isOnline && b.isOnline) return 1;
        const timeA = new Date(a.lastActiveAt || a.lastVisitedAt || 0).getTime();
        const timeB = new Date(b.lastActiveAt || b.lastVisitedAt || 0).getTime();
        return timeB - timeA;
      });

      onUpdate(list);
    },
      (err) => {
        const isOfflineError =
          err?.message?.includes('unavailable') ||
          err?.message?.includes('offline') ||
          err?.code === 'unavailable';
        if (!isOfflineError) {
          console.warn('Firestore visitor logs subscription notice:', err);
        }
        if (err?.code === 'permission-denied') {
          // Protected administrative telemetry - do not wipe existing logs with onUpdate([])
          return;
        }
        handleErr(err);
      }
    );
  }, onError);
}

const LOCAL_STORAGE_ANALYTICS_CACHE = 'nuestros_recuerdos_stats_cache';

/**
 * Fetches the authoritative, persistent traffic analytics from the server.
 * Guarantees monotonic historical data and never falls back to 0.
 */
export async function fetchAuthoritativeAnalytics(): Promise<WebAnalyticsData> {
  try {
    const res = await fetch('/api/analytics/stats');
    if (res.ok) {
      const json = await res.json();
      if (json.success && json.data) {
        const data: WebAnalyticsData = json.data;
        try {
          if (typeof window !== 'undefined') {
            localStorage.setItem(LOCAL_STORAGE_ANALYTICS_CACHE, JSON.stringify(data));
          }
        } catch {}
        return data;
      }
    }
  } catch (err) {
    console.warn('Network notice fetching analytics stats from server:', err);
  }

  // Fallback to cached analytics if available to preserve valid numbers
  try {
    if (typeof window !== 'undefined') {
      const cached = localStorage.getItem(LOCAL_STORAGE_ANALYTICS_CACHE);
      if (cached) {
        const parsed = JSON.parse(cached);
        return {
          ...parsed,
          status: 'updating',
        };
      }
    }
  } catch {}

  // Safe fallback structure
  return {
    activeUsersCount: 0,
    todayVisits: 0,
    weekVisits: 0,
    monthVisits: 0,
    yearVisits: 0,
    totalHistoricalVisits: 0,
    mobileCount: 0,
    desktopCount: 0,
    tabletCount: 0,
    mobilePercent: 0,
    desktopPercent: 0,
    tabletPercent: 0,
    dailyBuckets: [],
    maxDayCount: 1,
    recentLogs: [],
    status: 'loading',
  };
}

/**
 * Subscribes to real-time authoritative analytics updates.
 * Polls the backend every 10 seconds and prevents counters from resetting to 0.
 */
export function subscribeToAuthoritativeAnalytics(
  onUpdate: (data: WebAnalyticsData) => void,
  onError?: (err: Error) => void
): () => void {
  let isMounted = true;
  let lastKnownData: WebAnalyticsData | null = null;

  // 1. Deliver cached data immediately to prevent 0 or blank screen
  try {
    if (typeof window !== 'undefined') {
      const cached = localStorage.getItem(LOCAL_STORAGE_ANALYTICS_CACHE);
      if (cached) {
        lastKnownData = JSON.parse(cached);
        if (lastKnownData) {
          onUpdate(lastKnownData);
        }
      }
    }
  } catch {}

  const loadFresh = async () => {
    try {
      const fresh = await fetchAuthoritativeAnalytics();
      if (!isMounted) return;

      // Monotonic safety: Never overwrite valid historical data with an unverified empty reading
      if (
        lastKnownData &&
        lastKnownData.totalHistoricalVisits > 0 &&
        fresh.totalHistoricalVisits === 0 &&
        fresh.status !== 'available'
      ) {
        return;
      }

      lastKnownData = fresh;
      onUpdate(fresh);
    } catch (err: any) {
      if (onError) onError(err);
    }
  };

  loadFresh();

  const interval = setInterval(loadFresh, 10000);

  return () => {
    isMounted = false;
    clearInterval(interval);
  };
}

/**
 * Automatically logs or updates a visitor's session in backend & Firestore.
 * Deduplicates multiple visits per session per calendar day in America/Bogota.
 */
export async function recordVisitorVisit(visitor: VisitorInfo): Promise<void> {
  if (!visitor) return;

  const sessionId = visitor.visitorId || visitor.docId || 'dev_guest';
  let isNewSession = false;
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      if (!sessionStorage.getItem('analytics_visit_recorded')) {
        isNewSession = true;
        sessionStorage.setItem('analytics_visit_recorded', '1');
      }
    }
  } catch {}

  // 1. Authoritative Backend analytics registration
  try {
    fetch('/api/analytics/visit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        deviceType: visitor.deviceType || 'Escritorio',
        browser: visitor.browser || 'Navegador',
        os: visitor.os || 'Web',
        isNewSession,
      }),
    }).catch(() => {});
  } catch {}

  // 2. Real-time presence heartbeat registration in Firestore
  try {
    const presenceRef = doc(db, 'web_analytics_presence', sessionId);
    const now = new Date().toISOString();
    await setDoc(
      presenceRef,
      {
        sessionId,
        deviceType: visitor.deviceType || 'Escritorio',
        browser: visitor.browser || 'Navegador',
        os: visitor.os || 'Web',
        lastActiveAt: now,
        isOnline: true,
      },
      { merge: true }
    );
  } catch {}

  // 3. Fallback/Detailed visitor_logs recording if permitted
  try {
    const currentUid = auth.currentUser?.uid || (await signInPublicSession())?.uid;
    if (!currentUid || !visitor.docId) return;

    const docRef = doc(db, 'visitor_logs', visitor.docId);
    const snap = await getDoc(docRef).catch(() => null);
    const now = new Date().toISOString();

    if (snap && snap.exists()) {
      const existing = snap.data();
      const currentCount = typeof existing.visitCount === 'number' ? existing.visitCount : 1;

      await setDoc(
        docRef,
        sanitizeForFirestore({
          visitCount: isNewSession ? currentCount + 1 : currentCount,
          lastVisitedAt: now,
          lastActiveAt: now,
          isOnline: true,
        }),
        { merge: true }
      );
    } else {
      await setDoc(
        docRef,
        sanitizeForFirestore({
          ownerUid: currentUid,
          docId: visitor.docId,
          visitorId: visitor.visitorId || visitor.docId,
          ip: visitor.ip || 'Privada',
          country: visitor.country || '',
          countryCode: visitor.countryCode || '',
          city: visitor.city || '',
          region: visitor.region || '',
          flagEmoji: visitor.flagEmoji || '🌐',
          deviceType: visitor.deviceType || 'Desconocido',
          os: visitor.os || 'Desconocido',
          browser: visitor.browser || 'Desconocido',
          firstVisitedAt: now,
          lastVisitedAt: now,
          lastActiveAt: now,
          visitCount: 1,
          isOnline: true,
        })
      );
    }
  } catch (err) {
    // Non-blocking silent catch
  }
}

/**
 * Función obsoleta desactivada por política de privacidad (cero geolocalización)
 */
export async function updateVisitorPreciseLocation(): Promise<void> {
  // Desactivada permanentemente por privacidad
}

/**
 * Emits a real-time heartbeat to indicate the device is actively online on the site
 */
export async function recordVisitorHeartbeat(visitorDocId: string): Promise<void> {
  if (!visitorDocId) return;

  // 1. Authoritative Backend heartbeat
  try {
    fetch('/api/analytics/heartbeat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: visitorDocId }),
    }).catch(() => {});
  } catch {}

  // 2. Firestore presence heartbeat
  try {
    const presenceRef = doc(db, 'web_analytics_presence', visitorDocId);
    const now = new Date().toISOString();
    await setDoc(
      presenceRef,
      {
        sessionId: visitorDocId,
        lastActiveAt: now,
        isOnline: true,
      },
      { merge: true }
    );
  } catch {}

  // 3. visitor_logs doc heartbeat
  try {
    const docRef = doc(db, 'visitor_logs', visitorDocId);
    const now = new Date().toISOString();
    await setDoc(
      docRef,
      {
        lastActiveAt: now,
        isOnline: true,
      },
      { merge: true }
    );
  } catch {}
}

/**
 * Deletes a single visitor log from Firestore
 */
export async function deleteVisitorLogFromDb(id: string): Promise<void> {
  try {
    const docRef = doc(db, 'visitor_logs', id);
    await deleteDoc(docRef);
  } catch (err) {
    console.error('Error deleting visitor log from Firestore:', err);
    throw err;
  }
}

/**
 * Clears all visitor logs in Firestore and resets authoritative analytics store
 */
export async function clearAllVisitorLogsInDb(): Promise<void> {
  if (!isUserAdminSync(auth.currentUser)) {
    console.warn('Unauthorized clearAllVisitorLogsInDb invocation: Admin privileges required.');
    throw new Error('Operación no autorizada: Se requieren credenciales de administrador.');
  }

  // 1. Reset server-side analytics store
  try {
    await fetch('/api/analytics/reset', { method: 'POST' });
  } catch (e) {
    console.warn('Notice resetting server analytics:', e);
  }

  // 2. Clear local browser cache
  try {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(LOCAL_STORAGE_ANALYTICS_CACHE);
    }
  } catch {}

  // 3. Clear Firestore visitor logs
  try {
    const snapshot = await getDocs(VISITOR_LOGS_COLLECTION_REF);
    const batch = writeBatch(db);
    snapshot.forEach((d) => {
      batch.delete(d.ref);
    });
    await batch.commit();

    // Also clear presence docs
    try {
      const presenceSnap = await getDocs(collection(db, 'web_analytics_presence'));
      const pBatch = writeBatch(db);
      presenceSnap.forEach((d) => {
        pBatch.delete(d.ref);
      });
      await pBatch.commit();
    } catch {}
  } catch (err) {
    console.error('Error clearing visitor logs in Firestore:', err);
    throw err;
  }
}

// ----------------------------------------------------
// 9. WEB REQUESTS (MI WEB - ADQUISICIÓN DE PÁGINAS)
// ----------------------------------------------------
export function subscribeToWebRequests(
  onUpdate: (requests: MyWebRequest[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  // Security guard: visitors cannot query all private web requests
  if (!isUserAdminSync(auth.currentUser)) {
    onUpdate([]);
    return () => {};
  }

  return createResilientSubscription((handleErr) => {
    return onSnapshot(
      WEB_REQUESTS_COLLECTION_REF,
      (snapshot) => {
        const requests: MyWebRequest[] = [];
        snapshot.forEach((d) => {
          requests.push({ ...(d.data() as MyWebRequest), id: d.id });
        });
        requests.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        onUpdate(requests);
      },
      (err) => {
        const isOfflineError =
          err?.message?.includes('unavailable') ||
          err?.message?.includes('offline') ||
          err?.code === 'unavailable';
        if (!isOfflineError) {
          console.warn('Firestore web_requests subscription error:', err);
        }
        handleErr(err);
      }
    );
  }, onError);
}

/**
 * Real-time subscription to the authenticated user's own web requests
 * Strictly scoped by ownerUid to respect multi-tenant Firestore security rules.
 */
export function subscribeToUserWebRequests(
  ownerUid: string,
  onUpdate: (requests: MyWebRequest[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  if (!ownerUid) {
    onUpdate([]);
    return () => {};
  }

  return createResilientSubscription((handleErr) => {
    const q = query(WEB_REQUESTS_COLLECTION_REF, where('ownerUid', '==', ownerUid));
    return onSnapshot(
      q,
      (snapshot) => {
        const requests: MyWebRequest[] = [];
        snapshot.forEach((d) => {
          requests.push({ ...(d.data() as MyWebRequest), id: d.id });
        });
        requests.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        onUpdate(requests);
      },
      (err) => {
        const isOfflineError =
          err?.message?.includes('unavailable') ||
          err?.message?.includes('offline') ||
          err?.code === 'unavailable';
        if (!isOfflineError) {
          console.warn('Firestore user web_requests subscription error:', err);
        }
        handleErr(err);
      }
    );
  }, onError);
}

export async function publishWebToPublicDb(request: MyWebRequest): Promise<void> {
  if (!request.orderCode) return;
  const cleanCode = request.orderCode.trim().toUpperCase();
  const publicData = {
    orderCode: cleanCode,
    applicantName: request.applicantName || '',
    partnerName: request.partnerName || '',
    anniversaryDate: request.anniversaryDate || '',
    dedication: request.dedication || '',
    specialSong: request.specialSong || '',
    status: request.status || 'approved',
    customSettings: request.customSettings || {},
    customMemories: request.customMemories || [],
    customLetters: (request.customLetters || []).filter((l) => !l.isPrivate),
    customTulips: request.customTulips,
    customWatering: request.customWatering,
    updatedAt: new Date().toISOString(),
  };
  await setDoc(doc(db, 'public_websites', cleanCode), sanitizeForFirestore(publicData), { merge: true });
}

export async function getWebRequestByOrderCode(orderCode: string): Promise<MyWebRequest | null> {
  if (!orderCode) return null;
  const cleanCode = orderCode.trim().toUpperCase();

  // 1. Try public_websites first (safe public content, zero private leakage)
  try {
    const publicDocRef = doc(db, 'public_websites', cleanCode);
    const publicSnap = await getDoc(publicDocRef);
    if (publicSnap.exists()) {
      return { ...(publicSnap.data() as MyWebRequest), id: publicSnap.id };
    }
  } catch (publicErr) {
    // Silent catch, try authenticated web_requests if authorized
  }

  // 2. Try web_requests by orderCode or doc ID
  try {
    const q = query(WEB_REQUESTS_COLLECTION_REF, where('orderCode', '==', cleanCode), limit(1));
    const snap = await getDocs(q);
    if (!snap.empty) {
      const docSnap = snap.docs[0];
      return { ...(docSnap.data() as MyWebRequest), id: docSnap.id };
    }
    const docRef = doc(db, 'web_requests', orderCode);
    const byIdSnap = await getDoc(docRef);
    if (byIdSnap.exists()) {
      return { ...(byIdSnap.data() as MyWebRequest), id: byIdSnap.id };
    }
  } catch (err) {
    console.warn('Error fetching web request by order code:', err);
  }
  return null;
}

/**
 * Suscribe en tiempo real a una solicitud específica por su ID o código de orden
 */
export function subscribeToSingleWebRequest(
  idOrOrderCode: string,
  onUpdate: (request: MyWebRequest | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  if (!idOrOrderCode) {
    onUpdate(null);
    return () => {};
  }
  const clean = idOrOrderCode.trim();
  const cleanUpper = clean.toUpperCase();

  return createResilientSubscription((handleErr) => {
    // Si parece un ID de documento directo de Firebase
    if (clean.startsWith('req_')) {
      const docRef = doc(db, 'web_requests', clean);
      return onSnapshot(
        docRef,
        (snap) => {
          if (snap.exists()) {
            onUpdate({ ...(snap.data() as MyWebRequest), id: snap.id });
          } else {
            onUpdate(null);
          }
        },
        (err) => handleErr(err)
      );
    }

    // Consulta por orderCode
    const q = query(WEB_REQUESTS_COLLECTION_REF, where('orderCode', '==', cleanUpper), limit(1));
    return onSnapshot(
      q,
      (snapshot) => {
        if (!snapshot.empty) {
          const docSnap = snapshot.docs[0];
          onUpdate({ ...(docSnap.data() as MyWebRequest), id: docSnap.id });
        } else {
          // Intento fallback por ID de documento
          getDoc(doc(db, 'web_requests', clean))
            .then((snap) => {
              if (snap.exists()) {
                onUpdate({ ...(snap.data() as MyWebRequest), id: snap.id });
              } else {
                onUpdate(null);
              }
            })
            .catch(() => onUpdate(null));
        }
      },
      (err) => handleErr(err)
    );
  }, onError);
}

/**
 * Busca solicitudes en Firestore por código de orden (ej: WEB-8241 o 8241) o número telefónico (ej: 3001234567)
 * Consulta datos reales directamente de la base de datos sin simulaciones.
 */
export async function searchWebRequestsByCodeOrPhone(
  term: string
): Promise<MyWebRequest[]> {
  const cleanTerm = term.trim();
  if (!cleanTerm) return [];

  const resultsMap = new Map<string, MyWebRequest>();
  const upperTerm = cleanTerm.toUpperCase();
  const digitsOnly = cleanTerm.replace(/[^0-9]/g, '');

  try {
    // 1. Coincidencia exacta por orderCode (ej: "WEB-8241")
    const qCode = query(
      WEB_REQUESTS_COLLECTION_REF,
      where('orderCode', '==', upperTerm),
      limit(5)
    );
    const snapCode = await getDocs(qCode);
    snapCode.forEach((d) => resultsMap.set(d.id, { ...(d.data() as MyWebRequest), id: d.id }));

    // 2. Si el usuario ingresó solo números (ej: "8241"), buscar como "WEB-8241"
    if (digitsOnly.length >= 3 && digitsOnly.length <= 6 && !upperTerm.startsWith('WEB-')) {
      const qPrefix = query(
        WEB_REQUESTS_COLLECTION_REF,
        where('orderCode', '==', `WEB-${digitsOnly}`),
        limit(5)
      );
      const snapPrefix = await getDocs(qPrefix);
      snapPrefix.forEach((d) => resultsMap.set(d.id, { ...(d.data() as MyWebRequest), id: d.id }));
    }

    // 3. Buscar por ID de documento directo
    try {
      const docSnap = await getDoc(doc(db, 'web_requests', cleanTerm));
      if (docSnap.exists()) {
        resultsMap.set(docSnap.id, { ...(docSnap.data() as MyWebRequest), id: docSnap.id });
      }
    } catch {}

    // 4. Buscar por número de WhatsApp exacto
    const qPhoneExact = query(
      WEB_REQUESTS_COLLECTION_REF,
      where('whatsappPhone', '==', cleanTerm),
      limit(10)
    );
    const snapPhoneExact = await getDocs(qPhoneExact);
    snapPhoneExact.forEach((d) => resultsMap.set(d.id, { ...(d.data() as MyWebRequest), id: d.id }));

    // 5. Búsqueda por dígitos telefónicos o coincidencia de subcadena en las solicitudes registradas
    const recentQ = query(WEB_REQUESTS_COLLECTION_REF, limit(100));
    const recentSnap = await getDocs(recentQ).catch(() => null);
    if (recentSnap) {
      recentSnap.forEach((d) => {
        const data = d.data() as MyWebRequest;
        const phoneDigits = (data.whatsappPhone || '').replace(/[^0-9]/g, '');
        const orderCodeUpper = (data.orderCode || '').toUpperCase();
        
        // Coincidencia por código
        const matchesCode =
          orderCodeUpper === upperTerm ||
          orderCodeUpper.includes(upperTerm) ||
          upperTerm.includes(orderCodeUpper);

        // Coincidencia por teléfono
        const matchesPhone =
          digitsOnly.length >= 7 &&
          (phoneDigits.includes(digitsOnly) || digitsOnly.includes(phoneDigits));

        if (matchesCode || matchesPhone) {
          resultsMap.set(d.id, { ...data, id: d.id });
        }
      });
    }
  } catch (err) {
    console.warn('Error searching web requests by code or phone in Firestore:', err);
  }

  const list = Array.from(resultsMap.values());
  list.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  return list;
}

export async function saveWebRequestToDb(request: MyWebRequest): Promise<void> {
  try {
    const user = auth.currentUser || (await signInPublicSession());
    const validInitialStatuses = ['enviado', 'pending', 'pendiente', 'recibido'];
    const assignedStatus = request.status && validInitialStatuses.includes(request.status)
      ? request.status
      : 'recibido';

    const cleanRequest: MyWebRequest = {
      ...request,
      ownerUid: auth.currentUser?.uid || user?.uid || request.ownerUid,
      status: assignedStatus as WebRequestStatus,
    };

    // Safeguard: Ensure no administrative or escalation fields are included in initial creation
    delete (cleanRequest as any).adminNotes;
    delete (cleanRequest as any).adminDecision;
    delete (cleanRequest as any).adminUid;
    delete (cleanRequest as any).adminPin;
    delete (cleanRequest as any).adminPinHash;
    delete (cleanRequest as any).partner1PasswordHash;
    delete (cleanRequest as any).partner2PasswordHash;
    delete (cleanRequest as any).isPublished;
    delete (cleanRequest as any).approvedAt;
    delete (cleanRequest as any).publishedAt;
    delete (cleanRequest as any).rejectedAt;
    delete (cleanRequest as any).admin;
    delete (cleanRequest as any).role;
    delete (cleanRequest as any).permissions;

    const clean = sanitizeForFirestore(cleanRequest);
    const docRef = doc(db, 'web_requests', request.id);
    await setDoc(docRef, clean);

    // Publish public version if approved and caller has admin authorization
    if (request.status === 'approved' || request.status === 'aprobado') {
      if (isUserAdminSync(auth.currentUser)) {
        await publishWebToPublicDb(request).catch(() => {});
      }
    }
  } catch (err) {
    console.error('Error saving web request to Firestore:', err);
    throw err;
  }
}

export async function updateWebRequestStatusInDb(
  id: string,
  status: WebRequestStatus,
  adminNotes?: string,
  extraAdminFields?: Partial<MyWebRequest>
): Promise<void> {
  try {
    const docRef = doc(db, 'web_requests', id);
    const now = new Date().toISOString();
    const updateData: any = {
      status,
      updatedAt: now,
      ...(extraAdminFields || {}),
    };

    if (auth.currentUser?.uid) {
      updateData.adminUid = auth.currentUser.uid;
    }

    if (status === 'recibido') {
      updateData.receivedAt = now;
      updateData.allowUserEditing = false;
      updateData.changesLocked = true;
    } else if (status === 'en_revision') {
      updateData.inReviewAt = now;
      updateData.allowUserEditing = false;
      updateData.changesLocked = true;
    } else if (status === 'cambios_solicitados') {
      updateData.changesRequestedAt = now;
      updateData.allowUserEditing = true; // Desbloquea la edición para que el usuario responda
      updateData.changesLocked = false;
    } else if (status === 'approved' || status === 'aprobado') {
      updateData.approvedAt = now;
      updateData.allowUserEditing = false;
      updateData.changesLocked = true;
    } else if (status === 'publicado') {
      updateData.publishedAt = now;
      updateData.isPublished = true;
      updateData.allowUserEditing = false;
      updateData.changesLocked = true;
    } else if (status === 'rejected' || status === 'rechazado') {
      updateData.rejectedAt = now;
      updateData.allowUserEditing = false;
      updateData.changesLocked = true;
    }

    if (adminNotes !== undefined) {
      updateData.adminNotes = adminNotes;
    }

    await setDoc(docRef, sanitizeForFirestore(updateData), { merge: true });

    if (status === 'approved' || status === 'aprobado' || status === 'publicado') {
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        await publishWebToPublicDb({ ...(snap.data() as MyWebRequest), id, status }).catch(() => {});
      }
    }
  } catch (err) {
    console.error('Error updating web request status in Firestore:', err);
    throw err;
  }
}

/**
 * Submits user corrections/changes requested by the admin.
 * AUTOMATICALLY LOCKS EDITING: Transitions status to 'en_revision', sets allowUserEditing: false
 * and records changesSubmittedAt so no further edits can occur until the admin requests changes again.
 */
export async function submitUserChangesForWebRequest(
  id: string,
  data: Partial<MyWebRequest>
): Promise<void> {
  const now = new Date().toISOString();
  const updatePayload: Partial<MyWebRequest> = {
    ...data,
    status: 'en_revision',
    allowUserEditing: false, // Bloqueo automático inmediato
    changesLocked: true, // Bloqueo visual e interactivo
    changesSubmittedAt: now,
    updatedAt: now,
  };

  // 1. Escritura directa autorizada en Firestore vía SDK
  try {
    const docRef = doc(db, 'web_requests', id);
    const cleanData: any = sanitizeForFirestore(updatePayload);
    // Eliminar campos restringidos al administrador
    delete cleanData.adminPin;
    delete cleanData.adminPinHash;
    delete cleanData.partner1PasswordHash;
    delete cleanData.partner2PasswordHash;
    delete cleanData.orderCode;
    delete cleanData.adminNotes;
    delete cleanData.adminDecision;
    delete cleanData.adminUid;

    await setDoc(docRef, cleanData, { merge: true });
    return;
  } catch (firestoreErr) {
    console.warn('Direct Firestore setDoc notice, attempting backend fallback:', firestoreErr);
  }

  // 2. Respaldo vía endpoint del servidor
  try {
    const res = await fetch('/api/web-requests/submit-changes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, ...updatePayload }),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.success) return;
    }
  } catch (err) {
    console.error('Error submitting user changes to server:', err);
    throw err;
  }
}

/**
 * Updates an applicant's web request preferences (desired features, anniversary date, song, dedication, names, notes, userNotes)
 * Filtered to exclude administrator-restricted fields to adhere strictly to Firestore security rules.
 */
export async function updateUserWebRequestInDb(
  id: string,
  data: Partial<MyWebRequest>
): Promise<void> {
  try {
    const docRef = doc(db, 'web_requests', id);
    const cleanData: any = sanitizeForFirestore({
      ...data,
      updatedAt: new Date().toISOString(),
    });
    // Remove fields restricted to admin
    delete cleanData.status;
    delete cleanData.approvedAt;
    delete cleanData.publishedAt;
    delete cleanData.rejectedAt;
    delete cleanData.receivedAt;
    delete cleanData.inReviewAt;
    delete cleanData.changesRequestedAt;
    delete cleanData.adminNotes;
    delete cleanData.adminDecision;
    delete cleanData.adminUid;
    delete cleanData.orderCode;
    delete cleanData.ownerUid;
    delete cleanData.isPublished;
    delete cleanData.adminPin;
    delete cleanData.adminPinHash;
    delete cleanData.partner1PasswordHash;
    delete cleanData.partner2PasswordHash;

    await setDoc(docRef, cleanData, { merge: true });
  } catch (err) {
    console.error('Error updating user web request preferences in Firestore:', err);
    throw err;
  }
}

export async function deleteWebRequestFromDb(id: string): Promise<void> {
  try {
    const docRef = doc(db, 'web_requests', id);
    await deleteDoc(docRef);
    await deleteDoc(doc(db, 'public_websites', id.toUpperCase())).catch(() => {});
  } catch (err) {
    console.error('Error deleting web request from Firestore:', err);
    throw err;
  }
}

/**
 * Incrementa el contador global de descargas de la app en tiempo real en Firestore
 * y actualiza las descargas del enlace si se proporcionó un token o ID.
 */
export async function recordAppDownloadInDb(tokenOrId?: string): Promise<number> {
  try {
    const snap = await getDoc(PUBLIC_SETTINGS_DOC_REF);
    if (!snap.exists()) return 1;

    await signInPublicSession();
    const data = snap.data() as CoupleSettings;
    const currentTotal = typeof data.totalAppDownloads === 'number' ? data.totalAppDownloads : 0;
    const newTotal = currentTotal + 1;

    const currentLinks = Array.isArray(data.appDownloadLinks) ? data.appDownloadLinks : [];
    const updatedLinks = currentLinks.map((link) => {
      if (
        tokenOrId &&
        (link.id === tokenOrId ||
          link.token === tokenOrId ||
          (tokenOrId.length > 4 && (link.token || '').toLowerCase() === tokenOrId.toLowerCase()))
      ) {
        return {
          ...link,
          downloadsCount: (link.downloadsCount || 0) + 1,
        };
      }
      return link;
    });

    // Administrative authority check: only admins can update the links array directly
    // Visitors & non-admin users only update the public counter (totalAppDownloads)
    const isAdmin = isUserAdminSync(auth.currentUser);
    if (isAdmin) {
      await setDoc(
        PUBLIC_SETTINGS_DOC_REF,
        {
          totalAppDownloads: newTotal,
          appDownloadLinks: updatedLinks,
        },
        { merge: true }
      );
    } else {
      await setDoc(
        PUBLIC_SETTINGS_DOC_REF,
        {
          totalAppDownloads: newTotal,
        },
        { merge: true }
      );
    }

    return newTotal;
  } catch (err) {
    console.error('Error recording app download in Firestore:', err);
    return 1;
  }
}

/**
 * Obtiene los settings actuales de Firestore directamente (one-shot).
 */
export async function fetchSettingsOnce(): Promise<CoupleSettings | null> {
  try {
    const snap = await getDoc(PUBLIC_SETTINGS_DOC_REF);
    if (snap.exists()) {
      const data = snap.data() as CoupleSettings;
      if (typeof window !== 'undefined') {
        try {
          if (data.appDownloadLinks && Array.isArray(data.appDownloadLinks)) {
            localStorage.setItem('couple_app_links_cache', JSON.stringify(data.appDownloadLinks));
          }
        } catch {}
      }
      return data;
    }
    return null;
  } catch (err) {
    console.error('Error fetching settings once from Firestore:', err);
    return null;
  }
}

function checkLinkExpired(link: AppDownloadLink): boolean {
  if (!link.isActive) return true;
  if (!link.expiresAt) return false;
  return new Date(link.expiresAt).getTime() <= Date.now();
}

/**
 * Verifica de forma asíncrona e inmediata la existencia y validez de un token
 * de descarga en Firestore, evitando falsos negativos o parpadeos.
 */
export async function verifyAppDownloadTokenInDb(tokenToTest: string): Promise<{
  status: 'valid' | 'expired' | 'not_found';
  link: AppDownloadLink | null;
  settings: CoupleSettings | null;
}> {
  const cleanToken = (tokenToTest || '').trim().toLowerCase();

  // Intento de consulta directa en Firestore (con reintento de contingencia)
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      const snap = await getDoc(PUBLIC_SETTINGS_DOC_REF);
      if (snap.exists()) {
        const settingsData = snap.data() as CoupleSettings;
        const links = Array.isArray(settingsData.appDownloadLinks) ? settingsData.appDownloadLinks : [];

        // Actualizar caché de links para optimizar lecturas
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem('couple_app_links_cache', JSON.stringify(links));
          } catch {}
        }

        // Caso 1: No hay token en la URL -> buscar enlace permanente o activo general
        if (!cleanToken) {
          const activeLinks = links.filter((l) => l && l.isActive);
          const permanent = activeLinks.find((l) => l.duration === 'never');
          if (permanent) {
            return { status: 'valid', link: permanent, settings: settingsData };
          }
          const anyActive = activeLinks.find((l) => !checkLinkExpired(l));
          if (anyActive) {
            return { status: 'valid', link: anyActive, settings: settingsData };
          }
          return { status: 'not_found', link: null, settings: settingsData };
        }

        // Caso 2: Token proporcionado -> buscar coincidencia exacta o inclusiva
        const matched = links.find(
          (l) =>
            (l.token && l.token.toLowerCase() === cleanToken) ||
            (l.id && l.id.toLowerCase() === cleanToken) ||
            (cleanToken.length >= 6 &&
              l.token &&
              (l.token.toLowerCase().includes(cleanToken) || cleanToken.includes(l.token.toLowerCase())))
        );

        if (matched) {
          const expired = checkLinkExpired(matched);
          return {
            status: expired ? 'expired' : 'valid',
            link: matched,
            settings: settingsData,
          };
        }

        // Si no se encontró pero la lista vino vacía y es el primer intento, esperar breve sincronización
        if (links.length === 0 && attempt === 1) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          continue;
        }

        // Documento confirmado en Firestore y token no encontrado
        return { status: 'not_found', link: null, settings: settingsData };
      }
    } catch (err) {
      console.warn(`Intento ${attempt} de verificación de token en Firestore:`, err);
      if (attempt === 1) {
        await new Promise((resolve) => setTimeout(resolve, 350));
      }
    }
  }

  // Si hubo error de red en Firestore, revisar la caché local como respaldo
  if (typeof window !== 'undefined') {
    try {
      const cached = localStorage.getItem('couple_app_links_cache');
      if (cached) {
        const cachedLinks = JSON.parse(cached) as AppDownloadLink[];
        if (Array.isArray(cachedLinks) && cachedLinks.length > 0) {
          if (!cleanToken) {
            const active = cachedLinks.find((l) => l && l.isActive && !checkLinkExpired(l));
            if (active) return { status: 'valid', link: active, settings: null };
          }
          const matched = cachedLinks.find(
            (l) =>
              (l.token && l.token.toLowerCase() === cleanToken) ||
              (l.id && l.id.toLowerCase() === cleanToken)
          );
          if (matched) {
            return {
              status: checkLinkExpired(matched) ? 'expired' : 'valid',
              link: matched,
              settings: null,
            };
          }
        }
      }
    } catch {}
  }

  return { status: 'not_found', link: null, settings: null };
}
